import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { Sparkles, RefreshCw, Search, Loader2, Cpu, Zap, Link2, Unlink, Eye, AlertTriangle, Info } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TigoLayoutDialog } from "@/components/tigo/TigoLayoutDialog";

const n = (v, d = 1) => (v == null ? "—" : Number(v).toLocaleString("it-IT", { maximumFractionDigits: d }));

function Kpi({ label, value, testid }) {
  return (
    <Card className="flex flex-col gap-1 p-4" data-testid={testid}>
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-2xl font-extrabold tabular-nums">{value}</span>
    </Card>
  );
}

function LinkDialog({ system, onClose, onLinked }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const id = setTimeout(async () => {
      try { const res = await api.get("/tigo/plants-search", { params: { q } }); setResults(res.data?.plants || []); }
      catch { setResults([]); }
    }, 300);
    return () => clearTimeout(id);
  }, [q]);
  const pick = async (plant) => {
    setBusy(true);
    try {
      await api.post(`/tigo/systems/${system.id}/link`, { plant_id: plant.id });
      toast.success(`Collegato a ${plant.nome}`);
      onLinked(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Collegamento non riuscito"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="tigo-link-dialog">
        <DialogHeader><DialogTitle>Collega «{system.nome}» a un impianto</DialogTitle></DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca impianto per nome…" className="pl-9" data-testid="tigo-link-search" />
        </div>
        <div className="max-h-72 space-y-1 overflow-auto">
          {q.trim().length >= 2 && results.length === 0 && <p className="p-2 text-sm text-muted-foreground">Nessun impianto trovato.</p>}
          {results.map((p) => (
            <button key={p.id} disabled={busy} onClick={() => pick(p)} data-testid={`tigo-link-plant-${p.id}`}
              className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-muted disabled:opacity-50">
              <span className="font-medium">{p.nome}</span>
              <span className="text-xs text-muted-foreground">{[p.comune, p.provincia].filter(Boolean).join(" · ")}</span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function TigoMonitoringPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [linkFor, setLinkFor] = useState(null);
  const [layoutFor, setLayoutFor] = useState(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try { const res = await api.get("/tigo/systems"); setData(res.data); }
    catch (e) { setErr(e?.response?.data?.detail || "Impossibile leggere i sistemi Tigo"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const unlink = async (s) => {
    try { await api.delete(`/tigo/systems/${s.id}/link`); toast.success("Collegamento rimosso"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); }
  };

  const systems = data?.systems || [];
  const kpi = data?.kpi || {};
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return systems;
    return systems.filter((s) => [s.nome, s.comune, s.model, s.plant_nome].some((v) => (v || "").toLowerCase().includes(t)));
  }, [systems, q]);

  if (!user) return null;
  if (!["admin", "head_of_partner", "back_office", "technical"].includes(user.role) && !["back_office", "technical"].includes(user.role_actual) && !(user.extra_pages || []).includes("monitoring"))
    return <Navigate to="/" replace />;

  return (
    <div className="space-y-6 p-4 sm:p-8" data-testid="tigo-monitoring-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-3 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">
            <Sparkles className="h-8 w-8 text-[#3DA935]" />Tigo Monitoring
            <span className="rounded-full bg-[#3DA935]/15 px-3 py-1 text-xs font-bold uppercase tracking-wide text-[#3DA935]" data-testid="tigo-in-sviluppo">In sviluppo</span>
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">Ottimizzatori di potenza Tigo a livello di modulo, collegati alla rete Torri.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="tigo-refresh-btn">
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}Aggiorna
        </Button>
      </div>

      <Card className="flex items-start gap-3 border-[#3DA935]/30 bg-[#3DA935]/10 p-4 text-sm text-[#3DA935]" data-testid="tigo-demo-banner">
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-[#3DA935]" />
        <div>
          <b>Modulo dimostrativo.</b> Tigo non fornisce un ambiente demo via API: l'accesso reale richiede un impianto Tigo con abbonamento <i>Premium data</i>.
          I dati qui mostrati sono simulati e servono a presentare l'integrazione. Alla disponibilità di un impianto reale la sorgente dati verrà sostituita con le API Tigo mantenendo questa interfaccia.
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Kpi label="Sistemi" value={kpi.sistemi ?? "—"} testid="tigo-kpi-sistemi" />
        <Kpi label="Ottimizzatori" value={kpi.ottimizzatori ?? "—"} testid="tigo-kpi-ottimizzatori" />
        <Kpi label="Online" value={kpi.online ?? "—"} testid="tigo-kpi-online" />
        <Kpi label="Allarmi" value={kpi.allarme ?? "—"} testid="tigo-kpi-allarme" />
        <Kpi label="Offline" value={kpi.offline ?? "—"} testid="tigo-kpi-offline" />
        <Kpi label="Collegati" value={kpi.collegati_impianto ?? "—"} testid="tigo-kpi-collegati" />
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, comune, modello, impianto…" className="pl-9" data-testid="tigo-search-input" />
      </div>

      {err && <Card className="border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="tigo-error">{err}</Card>}

      {loading && !data ? (
        <div className="flex items-center gap-2 p-8 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" />Caricamento sistemi…</div>
      ) : (
        <div className="grid gap-3" data-testid="tigo-systems-list">
          {filtered.length === 0 && !err && <Card className="p-8 text-center text-sm text-muted-foreground" data-testid="tigo-empty">Nessun sistema trovato.</Card>}
          {filtered.map((s) => (
            <Card key={s.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`tigo-system-${s.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#3DA935]" />
                  <span className="truncate text-base font-bold" data-testid="tigo-system-name">{s.nome}</span>
                  {(s.allarme + s.offline) > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700 ring-1 ring-red-200"><AlertTriangle className="h-3 w-3" />{s.allarme + s.offline}</span>}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span>{[s.comune, s.provincia].filter(Boolean).join(" · ")}</span>
                  <span>{s.model}</span>
                  <span className="inline-flex items-center gap-1"><Cpu className="h-3 w-3" />{s.ottimizzatori} ottimizzatori</span>
                  <span>{s.rows}×{s.cols} · {s.module_wp} Wp</span>
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-3 text-xs">
                  {s.plant_id ? (
                    <>
                      <span className="inline-flex items-center gap-1 font-medium text-emerald-600" data-testid="tigo-system-collegato"><Link2 className="h-3 w-3" />Impianto: {s.plant_nome || s.plant_id}</span>
                      <button onClick={() => unlink(s)} data-testid={`tigo-unlink-${s.id}`} className="inline-flex items-center gap-1 text-slate-400 transition-colors hover:text-red-500"><Unlink className="h-3 w-3" />Scollega</button>
                    </>
                  ) : (
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setLinkFor(s)} data-testid={`tigo-link-btn-${s.id}`}><Link2 className="mr-1 h-3 w-3" />Collega a impianto</Button>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-3 text-right">
                <div className="flex items-center gap-1.5"><Zap className="h-5 w-5 text-[#3DA935]" /><span className="text-xl font-extrabold tabular-nums" data-testid="tigo-system-potenza">{n(s.potenza_dc_kw, 1)} kW</span></div>
                <Button size="sm" variant="outline" className="h-8" onClick={() => setLayoutFor(s)} data-testid={`tigo-layout-btn-${s.id}`}><Eye className="mr-1 h-4 w-4" />Layout</Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {linkFor && <LinkDialog system={linkFor} onClose={() => setLinkFor(null)} onLinked={load} />}
      {layoutFor && <TigoLayoutDialog title={`Tigo · ${layoutFor.nome}`} editable systemId={layoutFor.id} loader={() => api.get(`/tigo/systems/${layoutFor.id}/layout`).then((r) => r.data)} onClose={() => setLayoutFor(null)} />}
    </div>
  );
}
